import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

// Reusable premium primitives — keeps styling consistent across the app.

export const btn = {
  gold: "inline-flex items-center justify-center gap-2 bg-gold text-navy font-semibold px-7 py-3.5 hover:bg-gold-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-navy disabled:opacity-60",
  ghost: "inline-flex items-center justify-center gap-2 border border-white/25 text-white font-medium px-7 py-3.5 hover:bg-white/5 hover:border-gold/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold",
};

export function Spinner({ size = 28, className = "" }) {
  return <Loader2 className={`animate-spin text-gold ${className}`} size={size} aria-hidden="true" />;
}

export function PageLoader({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 gap-3" role="status" aria-live="polite" data-testid="page-loader">
      <Spinner size={32} />
      <span className="sr-only">{label}</span>
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-white/5 ${className}`} aria-hidden="true" />;
}

export function SectionHeading({ label, title, description, center = false, className = "" }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} ${className}`}>
      {label && (
        <div className={`flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-4 ${center ? "justify-center" : ""}`}>
          {!center && <span className="h-px w-8 bg-gold" />} {label}
        </div>
      )}
      <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight leading-tight max-w-3xl">{title}</h2>
      {description && <p className={`text-gray-400 mt-4 max-w-2xl leading-relaxed ${center ? "mx-auto" : ""}`}>{description}</p>}
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 32, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Chip({ children, active = false, ...props }) {
  return (
    <button
      {...props}
      className={`px-4 py-2 text-sm border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${active ? "bg-gold text-navy border-gold font-semibold" : "border-white/15 text-gray-300 hover:border-gold/50 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-16 border border-dashed border-white/10 bg-white/[0.02]" data-testid="empty-state">
      {Icon && <Icon className="text-gray-600 mx-auto mb-4" size={32} aria-hidden="true" />}
      <h3 className="font-heading text-xl text-white mb-2">{title}</h3>
      {description && <p className="text-gray-500 mb-6 max-w-md mx-auto">{description}</p>}
      {action}
    </div>
  );
}
